/**
 * Score trend: simple inline SVG (tokens only via CSS classes, no colour literals). The data is also given as a
 * visually-hidden table so screen readers get every value; the chart itself is one labelled image.
 */
import { useId } from 'react';
import { useApp } from '../../../../../lib/app-context';
import { useFormat } from '../../../../../lib/format';
import type { TrendPoint } from '../actions/client';

export const CHART = { w: 600, h: 220, pad: { top: 16, right: 16, bottom: 28, left: 36 } } as const;

/** Points scaled to the chart box (score 0-100 on y, runs evenly spaced on x). */
export function scalePoints(points: readonly TrendPoint[], c = CHART): { x: number; y: number; score: number; at: string }[] {
  const iw = c.w - c.pad.left - c.pad.right;
  const ih = c.h - c.pad.top - c.pad.bottom;
  return points.map((p, i) => ({
    x: Math.round((c.pad.left + (points.length === 1 ? iw / 2 : (iw * i) / (points.length - 1))) * 10) / 10,
    y: Math.round((c.pad.top + ih * (1 - Math.max(0, Math.min(100, p.score)) / 100)) * 10) / 10,
    score: p.score, at: p.at,
  }));
}

export function TrendChart({ points }: { points: readonly TrendPoint[] }) {
  const { t } = useApp();
  const f = useFormat();
  const id = useId();
  const pts = scalePoints(points);
  const first = points[0];
  const last = points[points.length - 1];
  const summary = first && last ? t('audit-remediation.monitoring.trend.summary', { from: first.score, to: last.score, n: points.length }) : '';
  const yOf = (v: number) => CHART.pad.top + (CHART.h - CHART.pad.top - CHART.pad.bottom) * (1 - v / 100);
  return (
    <figure className="ar-trend" data-testid="trend-chart">
      <svg viewBox={`0 0 ${CHART.w} ${CHART.h}`} role="img" aria-labelledby={`${id}-t ${id}-d`} preserveAspectRatio="xMidYMid meet" direction="ltr">
        <title id={`${id}-t`}>{t('audit-remediation.monitoring.trend.title')}</title>
        <desc id={`${id}-d`}>{summary}</desc>
        {[0, 50, 100].map((v) => (
          <g key={v} className="ar-trend__grid">
            <line x1={CHART.pad.left} x2={CHART.w - CHART.pad.right} y1={yOf(v)} y2={yOf(v)} />
            <text x={CHART.pad.left - 6} y={yOf(v) + 4} textAnchor="end">{v}</text>
          </g>
        ))}
        {pts.length > 1 ? <polyline className="ar-trend__line" points={pts.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" /> : null}
        {pts.map((p, i) => <circle key={i} className="ar-trend__dot" data-last={i === pts.length - 1 ? 'true' : undefined} cx={p.x} cy={p.y} r={i === pts.length - 1 ? 5 : 3.5} />)}
        {first ? <text className="ar-trend__axis" x={CHART.pad.left} y={CHART.h - 6}>{f.date(first.at)}</text> : null}
        {last && points.length > 1 ? <text className="ar-trend__axis" x={CHART.w - CHART.pad.right} y={CHART.h - 6} textAnchor="end">{f.date(last.at)}</text> : null}
      </svg>
      <figcaption className="ar-muted">{summary}</figcaption>
      <table className="qm-sr-only">
        <caption>{t('audit-remediation.monitoring.trend.title')}</caption>
        <thead><tr><th scope="col">{t('audit-remediation.monitoring.trend.date')}</th><th scope="col">{t('audit-remediation.monitoring.trend.score')}</th></tr></thead>
        <tbody>{points.map((p) => <tr key={p.at}><td>{f.date(p.at)}</td><td>{p.score}</td></tr>)}</tbody>
      </table>
    </figure>
  );
}
